import { cloneDeep } from '../libs/cloneDeep';
import { assetNameComponentsFromName, assetNameFromNameComponents } from '../common';
import uuid from './utils/uuid';
import type { OpaqueAsset, RawAssetData } from './types';

/**
 * Returns an ordered list of duplicated OpaqueAssets, where the  OpaqueVideoData[0] === topParent
 *
 * This function supports a flat tree of unordered OpaqueAssets.
 * It makes sure to duplicate every child asset and generate new "id" and name.
 * It rebuilds the relationship with the new "ids". It works like a clone.
 *
 * Note: This function supports name collision prevention.
 */

function duplicateAssetData(
    opaqueAssets: OpaqueAsset[] | RawAssetData[],
    blackListNames: string[] = [],
): OpaqueAsset[] {
    const rawAssets = opaqueAssets as RawAssetData[];

    if (!rawAssets.length) {
        return [];
    }

    // initializing the hash memory by holding the original top parentId
    // this way we can receive an unsorted list of OpaqueAssets[] and it will duplicate
    // every element correctly and maintain the correct old and new keys
    // MapStructure { [originalId]: {newId} } PS: The id's are generated with "uuid()"
    // e.g: { "b76b7581-6a08-9aee-a75d-66c47f32d1c6": ""b76b7581-6a08-9aee-a75d-66c47f32d1f3"" }
    const assetOriginalParentIds = {};

    const rawAssetNames = rawAssets.map((asset) => asset.name);
    const uniqueNames = new Set([...rawAssetNames, ...blackListNames]);

    // Track the highest count for each root to prevent name collision.
    // MapStructure { [root]: highestCount }
    // e.g: { 'text': 1, 'group': 2 }
    const highestCountByRoot: Record<string, number> = {};
    for (const name of uniqueNames) {
        const { root, count } = assetNameComponentsFromName(name);
        if (!highestCountByRoot[root] || count > highestCountByRoot[root]) {
            highestCountByRoot[root] = count;
        }
    }

    const final = [];

    for (let i = 0; i < rawAssets.length; i++) {
        const currentAsset = rawAssets[i];
        const parentId = assetOriginalParentIds[currentAsset.parentId];
        const hasNoParent = !currentAsset.parentId;

        const { root, suffix } = assetNameComponentsFromName(currentAsset.name);
        const newCount = (highestCountByRoot[root] || 0) + 1;
        highestCountByRoot[root] = newCount;
        const newName = assetNameFromNameComponents({ root, count: newCount, suffix });
        const newId = uuid();
        const clone = cloneDeep(currentAsset);

        if (rawAssets.length === 1 || hasNoParent) {
            final.unshift({
                ...clone,
                id: newId,
                name: newName,
                parentId: null,
            });
        } else {
            final.push({
                ...clone,
                id: newId,
                parentId,
                name: newName,
            });
        }

        assetOriginalParentIds[currentAsset.id] = newId;
    }

    return final;
}

export default duplicateAssetData;
